import { call, put, takeEvery } from 'redux-saga/effects';
import {
    FETCH_MAILING_LIST_REQUEST,
    FETCH_MAILING_LIST_SUCCESS,
    FETCH_MAILING_LIST_FAILURE,
    CREATE_MAILING_LIST_REQUEST,
    CREATE_MAILING_LIST_SUCCESS,
    CREATE_MAILING_LIST_FAILURE,
    UPDATE_MAILING_LIST_REQUEST,
    UPDATE_MAILING_LIST_SUCCESS,
    UPDATE_MAILING_LIST_FAILURE,
    DELETE_MAILING_LIST_REQUEST,
    DELETE_MAILING_LIST_SUCCESS,
    DELETE_MAILING_LIST_FAILURE,
} from '../actions/mailingListActions';
import { getMailingList, createMailingList, updateMailingListEntry, deleteMailingListEntry } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';

function* fetchMailingListSaga(action) {
    try {
        // Utiliser forceRefresh si spécifié dans l'action
        const forceRefresh = action.payload?.forceRefresh || false;
        const data = yield call(getMailingList, { forceRefresh });
        yield put({ type: FETCH_MAILING_LIST_SUCCESS, payload: data });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors du chargement de la mailing list');
        yield put({ type: FETCH_MAILING_LIST_FAILURE, payload: errorMessage });
    }
}

function* createMailingListSaga(action) {
    try {
        const mailingListData = action.payload;
        const response = yield call(createMailingList, mailingListData);
        
        // Gérer la réponse 201 avec la structure { message, data }
        const data = response?.data || response;
        yield put({ type: CREATE_MAILING_LIST_SUCCESS, payload: data });
        
        // Rafraîchir la liste depuis le serveur pour s'assurer d'avoir les données à jour
        // Utiliser forceRefresh pour contourner le cache
        const refreshedData = yield call(getMailingList, { forceRefresh: true });
        yield put({ type: FETCH_MAILING_LIST_SUCCESS, payload: refreshedData });
    } catch (error) {
        // Gestion spécifique des erreurs pour la mailing list
        let errorMessage = 'Erreur lors de la création de l\'entrée dans la mailing list';
        
        if (error.response) {
            const status = error.response.status;
            const responseData = error.response.data;
            
            // Extraire le message d'erreur selon le format de l'API
            if (responseData?.detail) {
                // Le détail peut être une chaîne ou un objet
                if (typeof responseData.detail === 'string') {
                    errorMessage = responseData.detail;
                } else if (Array.isArray(responseData.detail)) {
                    // Format FastAPI/Pydantic avec tableau d'erreurs
                    errorMessage = responseData.detail
                        .map((err) => {
                            if (err.msg) {
                                const field = err.loc && err.loc.length > 1 ? err.loc[err.loc.length - 1] : '';
                                return field ? `${field}: ${err.msg}` : err.msg;
                            }
                            return JSON.stringify(err);
                        })
                        .filter(Boolean)
                        .join('; ');
                } else if (responseData.detail.message) {
                    errorMessage = responseData.detail.message;
                } else {
                    errorMessage = JSON.stringify(responseData.detail);
                }
            } else if (responseData?.message) {
                errorMessage = responseData.message;
            } else {
                // Utiliser le gestionnaire d'erreur par défaut
                errorMessage = handleApiError(error, errorMessage);
            }
        } else {
            // Erreur réseau ou autre
            errorMessage = error.message || errorMessage;
        }
        
        yield put({ type: CREATE_MAILING_LIST_FAILURE, payload: errorMessage });
    }
}

function* updateMailingListSaga(action) {
    try {
        const { emailId, mailingListData } = action.payload;
        const response = yield call(updateMailingListEntry, emailId, mailingListData);
        
        // Gérer la réponse avec la structure { message, data } ou directement l'entrée
        const data = response?.data || response;
        yield put({ type: UPDATE_MAILING_LIST_SUCCESS, payload: data });
        
        // Rafraîchir la liste depuis le serveur pour s'assurer d'avoir les données à jour
        // Utiliser forceRefresh pour contourner le cache
        const refreshedData = yield call(getMailingList, { forceRefresh: true });
        yield put({ type: FETCH_MAILING_LIST_SUCCESS, payload: refreshedData });
    } catch (error) {
        // Gestion spécifique des erreurs pour la mise à jour
        let errorMessage = 'Erreur lors de la mise à jour de l\'entrée de la mailing list';
        
        if (error.response) {
            const responseData = error.response.data;
            
            // Extraire le message d'erreur selon le format de l'API
            if (responseData?.detail) {
                if (typeof responseData.detail === 'string') {
                    errorMessage = responseData.detail;
                } else if (Array.isArray(responseData.detail)) {
                    errorMessage = responseData.detail
                        .map((err) => {
                            if (err.msg) {
                                const field = err.loc && err.loc.length > 1 ? err.loc[err.loc.length - 1] : '';
                                return field ? `${field}: ${err.msg}` : err.msg;
                            }
                            return JSON.stringify(err);
                        })
                        .filter(Boolean)
                        .join('; ');
                } else if (responseData.detail.message) {
                    errorMessage = responseData.detail.message;
                } else {
                    errorMessage = JSON.stringify(responseData.detail);
                }
            } else if (responseData?.message) {
                errorMessage = responseData.message;
            } else {
                errorMessage = handleApiError(error, errorMessage);
            }
        } else {
            errorMessage = error.message || errorMessage;
        }
        
        yield put({ type: UPDATE_MAILING_LIST_FAILURE, payload: errorMessage });
        
        // En cas d'erreur, rafraîchir la liste pour restaurer l'état correct
        try {
            const refreshedData = yield call(getMailingList, { forceRefresh: true });
            yield put({ type: FETCH_MAILING_LIST_SUCCESS, payload: refreshedData });
        } catch (refreshError) {
            console.error('Erreur lors du rafraîchissement après mise à jour:', refreshError);
        }
    }
}

function* deleteMailingListSaga(action) {
    const emailId = action.payload;
    try {
        yield call(deleteMailingListEntry, emailId);
        yield put({ type: DELETE_MAILING_LIST_SUCCESS, payload: emailId });
        
        // Rafraîchir la liste depuis le serveur pour s'assurer d'avoir les données à jour
        // Utiliser forceRefresh pour contourner le cache
        const refreshedData = yield call(getMailingList, { forceRefresh: true });
        yield put({ type: FETCH_MAILING_LIST_SUCCESS, payload: refreshedData });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors de la suppression de l\'entrée de la mailing list');
        yield put({ type: DELETE_MAILING_LIST_FAILURE, payload: errorMessage });
        
        // En cas d'erreur, rafraîchir la liste pour restaurer l'état correct
        try {
            const refreshedData = yield call(getMailingList, { forceRefresh: true });
            yield put({ type: FETCH_MAILING_LIST_SUCCESS, payload: refreshedData });
        } catch (refreshError) {
            // Si le rafraîchissement échoue aussi, on laisse l'erreur de suppression principale
            console.error('Erreur lors du rafraîchissement après suppression:', refreshError);
        }
    }
}

export function* watchMailingListRequests() {
    yield takeEvery(FETCH_MAILING_LIST_REQUEST, fetchMailingListSaga);
    yield takeEvery(CREATE_MAILING_LIST_REQUEST, createMailingListSaga);
    yield takeEvery(UPDATE_MAILING_LIST_REQUEST, updateMailingListSaga);
    yield takeEvery(DELETE_MAILING_LIST_REQUEST, deleteMailingListSaga);
}
